// WhatsApp overview (E look): sender strip, registrations + people bars (legend + funnel), Needs attention, health line.
import {
  ArrowClockwise,
  ArrowRight,
  ArrowsClockwise,
  CaretDown,
  CaretRight,
  ChatsCircle,
  FileText,
  HourglassMedium,
  PaperPlaneTilt,
  type Icon as PhIcon,
  PhoneX,
  WarningCircle,
} from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { type CSSProperties, type HTMLAttributes, type ReactNode, useMemo, useState } from "react";
import { Ic, Journey, useTick } from "@/components/bits";
import { CountUp, SenderStrip } from "@/components/shared";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSyncedAt } from "@/data/live";
import { retryOverLimit } from "@/data/queries";
import { EVENT } from "@/lib/fields";
import { ago, dur, fmt } from "@/lib/format";
import { windowLeft } from "@/model/cap";
import { isWaiting, segmentCounts } from "@/model/overview";
import type { Person } from "@/model/people";
import { type RetryQueue, retryCopy } from "@/model/runs";
import { sessionBreakdown, tailText } from "@/model/sessions";
import type { PStatus } from "@/model/status";
import { useApp } from "@/shell/appData";
import { useToast } from "@/shell/toast";

export function Overview() {
  const { people, counts, config, retry } = useApp();
  const now = useTick(30_000);
  const synced = useSyncedAt();
  const total = counts.people;
  const regs = counts.regs;
  const soonest = people
    .filter(isWaiting)
    .map((p) => windowLeft(p.conv, now))
    .sort((a, b) => a - b)[0];
  const att: {
    icon: PhIcon;
    cls: string;
    j: PStatus | null;
    n: number;
    t: string;
    s: string;
    s_: string;
    b: string;
    search: { v?: "attention"; s?: string };
  }[] = [
    {
      icon: ChatsCircle,
      cls: "pri",
      j: null,
      n: counts.waiting,
      t: `${counts.waiting} ${counts.waiting === 1 ? "reply" : "replies"} waiting`,
      s: counts.waiting
        ? soonest && soonest > 0
          ? `Soonest reply window closes in ${dur(soonest)}`
          : "Reply windows have closed"
        : "Everyone has an answer",
      s_: "waiting",
      b: "Reply",
      search: { v: "attention", s: "waiting" },
    },
    {
      icon: PhoneX,
      cls: "bad",
      j: "invalid",
      n: counts.byStatus.invalid,
      t: `${counts.byStatus.invalid} invalid phone numbers`,
      s: "Fix them in the sheet, run wa publish, and they join the next batch",
      s_: "invalid",
      b: "Fix",
      search: { v: "attention", s: "invalid" },
    },
    {
      icon: WarningCircle,
      cls: "bad",
      j: "failed",
      n: counts.byStatus.failed,
      t: `${counts.byStatus.failed} failed`,
      s: "WhatsApp could not deliver. Check the number or call them.",
      s_: "failed",
      b: "Check",
      search: { v: "attention", s: "failed" },
    },
    {
      icon: ArrowClockwise,
      cls: "warn",
      j: "retrying",
      n: counts.byStatus.retrying,
      t: `${counts.byStatus.retrying} retrying`,
      s: "Nothing reached WhatsApp yet. The next send run tries again.",
      s_: "retrying",
      b: "View",
      search: { s: "retrying" },
    },
  ];

  return (
    <div className="page compact e-look">
      <div className="pghead">
        <div className="t">
          <h1>WhatsApp</h1>
          <p>Registration confirmations · {EVENT.name}</p>
        </div>
        <div className="acts">
          <Link className="btn primary" to="/send">
            <Ic i={PaperPlaneTilt} />
            Send
          </Link>
        </div>
      </div>
      <SenderStrip />
      <StatusCard
        title="Registrations"
        sub="matches the Google Sheet"
        unit="registrations"
        total={counts.registrations}
        messaged={regs.messaged}
        notSent={regs.byStatus.queued}
        seg={segmentCounts(regs.byStatus)}
        got={regs.got}
        read={regs.read}
        testId="reg-card"
      />
      <SessionCard people={people} />
      <StatusCard
        title="People (one WhatsApp number each)"
        unit="people"
        total={total}
        messaged={counts.messaged}
        notSent={counts.byStatus.queued}
        seg={segmentCounts(counts.byStatus)}
        got={counts.got}
        read={counts.read}
        testId="people-card"
        linked
        note="A family shares one number; a person shows their least-advanced message."
      />
      <div className="section">
        <div className="section-h">
          <h2>Needs attention</h2>
          <span className="sp" />
          <Link className="link sm" to="/people" search={{ v: "attention" }}>
            Open all
          </Link>
        </div>
        <div className="list">
          <RetryRow q={retry} now={now} />
          {att.map((a) => (
            <Link key={a.s_} className={`row${a.n ? "" : " zero"}`} to="/people" search={a.search}>
              <span className={`ic ${a.cls}`}>
                <Ic i={a.icon} w="fill" />
              </span>
              <span className="tx">
                <b>{a.t}</b>
                <span>{a.s}</span>
              </span>
              {a.j ? (
                <span className="hide-sm">
                  <Journey s={a.j} />
                </span>
              ) : null}
              <span className="btn line xs hide-sm">{a.b}</span>
              <Ic i={CaretRight} className="chev" />
            </Link>
          ))}
        </div>
      </div>
      <div className="health">
        <Link to="/settings">
          <Ic i={FileText} />
          {config?.template ? <span className="mono">{config.template}</span> : "No config published yet"}
        </Link>
        {config?.publishedAt ? (
          <Link to="/settings">
            <span className="dot" />
            List published {ago(config.publishedAt, now)}
          </Link>
        ) : null}
        <span>
          <Ic i={ArrowsClockwise} />
          Synced&nbsp;<span>{ago(synced, now)}</span>
        </span>
      </div>
    </div>
  );
}

const pct = (a: number, b: number) => (b ? ((a / b) * 100).toFixed(1) : "0.0");

/** A People `?s=` link when linked, else a plain span with the same look. */
function To({
  linked,
  s,
  className,
  children,
  ...rest
}: { linked?: boolean; s: string; className?: string; children?: ReactNode } & HTMLAttributes<HTMLElement>) {
  return linked ? (
    <Link className={className} to="/people" search={{ s }} {...rest}>
      {children}
    </Link>
  ) : (
    <span className={className} {...rest}>
      {children}
    </span>
  );
}

/**
 * Headline + segmented bar + legend + funnel. Used per registration and per person.
 * `linked`: segments open People filtered by `?s=`; only true for the people card (those filters count people).
 */
function StatusCard(props: {
  title: string;
  sub?: string;
  unit: string;
  total: number;
  messaged: number;
  notSent: number;
  seg: ReturnType<typeof segmentCounts>;
  got: number;
  read: number;
  testId: string;
  linked?: boolean;
  note?: string;
}) {
  const { title, sub, unit, total, messaged, notSent, seg, got, read, testId, linked, note } = props;
  return (
    <div className="card ov" data-testid={testId}>
      <div className="ov-top">
        <div>
          <div className="eyebrow">
            {title}
            {sub ? <span className="ov-sub"> · {sub}</span> : null}
          </div>
          <div className="ov-big">
            <span>
              <CountUp n={total} />
              {unit}
            </span>
            <span>
              <CountUp n={messaged} />
              messaged
            </span>
            <span>
              <CountUp n={notSent} />
              not sent yet
            </span>
          </div>
        </div>
        <Link className="btn ghost xs hide-sm" to="/people" search={{}}>
          Open list
          <Ic i={ArrowRight} />
        </Link>
      </div>
      <div className="stack lg">
        {seg
          .filter((s) => s.n)
          .map((s, i) => (
            <To
              key={s.key}
              linked={linked}
              s={s.key}
              style={
                {
                  width: `calc(${(s.n / Math.max(total, 1)) * 100}% - 3px)`,
                  minWidth: 4,
                  background: s.color,
                  "--i": i,
                } as CSSProperties
              }
              title={`${s.label}: ${fmt(s.n)}`}
              aria-label={`${s.label}: ${fmt(s.n)}`}
            />
          ))}
      </div>
      <div className="lgp-wrap" data-testid="legend">
        {seg.map((s) => (
          <To
            linked={linked}
            key={s.key}
            className={linked ? "lgp" : "lgp static"}
            s={s.key}
            {...{ "data-seg": s.key }}
          >
            <span className="sw" style={{ background: s.color }} />
            {s.label}
            <b>{fmt(s.n)}</b>
          </To>
        ))}
      </div>
      {note ? <p className="ov-note">{note}</p> : null}
      <div className="ov-fn">
        <span>Of {fmt(messaged)} messaged</span>
        <To linked={linked} s="got">
          <b>{pct(got, messaged)}%</b> delivered
        </To>
        <To linked={linked} s="read">
          <b>{pct(read, got)}%</b> of those read
        </To>
      </div>
    </div>
  );
}

const SESSIONS_OPEN_KEY = "ov.sessions.open";

function readOpen(): boolean {
  try {
    return localStorage.getItem(SESSIONS_OPEN_KEY) !== "0";
  } catch {
    return true; // storage blocked: open by default
  }
}

/**
 * "Registrations by session": one row per session with the Registrations card's bar and a short tail.
 * Collapsible, open by default, remembered per browser. Hidden until the snapshot carries sessions.
 */
function SessionCard({ people }: { people: Person[] }) {
  const b = useMemo(() => sessionBreakdown(people), [people]);
  const [open, setOpen] = useState(readOpen);
  if (!b.known) return null;
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      localStorage.setItem(SESSIONS_OPEN_KEY, next ? "1" : "0");
    } catch {
      // storage blocked: the toggle still works for this visit
    }
  };
  return (
    <div className="card ov ses" data-testid="session-card">
      <button type="button" className="ses-h" aria-expanded={open} aria-controls="ses-body" onClick={toggle}>
        <span className="eyebrow">Registrations by session</span>
        <span className="ses-toggle">
          {open ? "Collapse" : "Expand"}
          <Ic i={CaretDown} className={`ses-caret${open ? "" : " shut"}`} />
        </span>
      </button>
      {open ? (
        <div id="ses-body" className="ses-b">
          {b.rows.map((r) => (
            <div key={r.key} className="ses-row" data-testid="session-row" data-session={r.key}>
              <span className="ses-l">
                <b>{r.label}</b>
                {r.time ? <span className="ses-t">{r.time}</span> : null}
              </span>
              <b className="ses-n">{fmt(r.total)}</b>
              <div className="stack ses-bar">
                {r.seg
                  .filter((s) => s.n)
                  .map((s) => (
                    <span
                      key={s.key}
                      role="img"
                      style={{
                        width: `calc(${(s.n / Math.max(r.total, 1)) * 100}% - 2px)`,
                        minWidth: 3,
                        background: s.color,
                      }}
                      title={`${s.label}: ${fmt(s.n)}`}
                      aria-label={`${s.label}: ${fmt(s.n)}`}
                    />
                  ))}
              </div>
              <span className="ses-tail">{tailText(r.tail, fmt)}</span>
            </div>
          ))}
          <p className="ov-note">Someone attending several sessions counts in each one.</p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Needs attention: "N over the limit · waiting for retry" + Retry all → confirm → wa_retry → toast.
 * The text opens People filtered to over_limit; the button is off when the sender has not run lately.
 */
function RetryRow({ q, now }: { q: RetryQueue; now: number }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const c = retryCopy(q, now);
  const go = async () => {
    setBusy(true);
    try {
      const res = await retryOverLimit(q.keys);
      toast(`${res.data.released} queued for resend`);
      setOpen(false);
    } catch (e) {
      toast(`Retry failed: ${(e as Error).message || "error"}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`row retry-row${q.keys.length ? "" : " zero"}`} data-testid="retry-row">
      <Link className="rl" to="/people" search={{ v: "attention", s: "over_limit" }}>
        <span className="ic warn">
          <Ic i={HourglassMedium} w="fill" />
        </span>
        <span className="tx">
          <b>{c.title}</b>
          <span>{c.sub}</span>
        </span>
      </Link>
      <span title={q.why ?? undefined}>
        <button
          type="button"
          className="btn line xs"
          disabled={!q.canRetry || busy}
          onClick={() => setOpen(true)}
          data-testid="retry-all"
        >
          Retry all
        </button>
      </span>
      <Link to="/people" search={{ v: "attention", s: "over_limit" }} aria-label="Open over-limit people">
        <Ic i={CaretRight} className="chev" />
      </Link>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>{c.confirmTitle}</DialogTitle>
            <DialogDescription>{c.confirmBody}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <button type="button" className="btn line" disabled={busy}>
                Cancel
              </button>
            </DialogClose>
            <button type="button" className="btn primary" disabled={busy} onClick={go} data-testid="retry-confirm">
              {busy ? "Queuing…" : "Resend"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
